import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { FadeLoader } from 'react-spinners';
import companyLogo from '../kantei-logo.svg';
import appLogo from '../kantei-mobile-logo.svg';
import './MansionVideoShare.css';
import { queryRequest } from '../services/internalApi';
import { useAnalytics } from '../hooks/analytics/useAnalytics';
import { SCREEN_NAMES } from '../constants/analytics';

const ERROR_MESSAGES = {
    400: 'URLが正しくありません。\n共有されたリンクをもう一度ご確認ください。',
    404: 'この動画はご覧いただけません。\n共有が停止されているか、削除された可能性があります。',
    500: '一時的なエラーが発生しました。\n時間をおいて再度お試しください。',
};
const DEFAULT_ERROR_MESSAGE = ERROR_MESSAGES[500];

function MansionVideoShare()
{
    const [loading, setLoading] = useState(true);
    const [playbackUrl, setPlaybackUrl] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const languageType = searchParams.get('languageType');

    // ページ表示情報をアナリティクスに送信する
    const analytics = useAnalytics();

    useEffect(() => {
        if (!token || !languageType) {
            setLoading(false);
            setErrorMessage(ERROR_MESSAGES[400]);
            return;
        }

        queryRequest(`
            query ($token: String!, $languageType: String!) {
                getMansionVideoShareContent(
                    token: $token,
                    languageType: $languageType
                ) {
                    common {
                        statusCode
                        message
                    }
                    playbackUrl
                }
            }
        `, {
            variables: { token, languageType }
        })
            .then((result) => {
                setLoading(false);

                const { statusCode } = result.data.getMansionVideoShareContent.common;
                analytics.trackScreenView(SCREEN_NAMES.MANSION_VIDEO_SHARE, { status_code: statusCode });

                if (statusCode !== 200) {
                    setErrorMessage(ERROR_MESSAGES[statusCode] ?? DEFAULT_ERROR_MESSAGE);
                    return;
                }

                setPlaybackUrl(result.data.getMansionVideoShareContent.playbackUrl);
            })
            .catch((error) => {
                setLoading(false);
                setErrorMessage(DEFAULT_ERROR_MESSAGE);
            });
        // マウント時に一度だけ実行する意図的な設計(analyticsはuseMemoで参照が安定しているため省略可)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <HelmetProvider>
            <Helmet>
                <title>カンテイモバイル</title>
            </Helmet>
            <div className="App">
                <div className="App-background">
                    <div className="App-base">
                        {loading && (
                            <div className="App-loading">
                                <FadeLoader
                                    color="#78D7FF"
                                    loading={loading}
                                    size={100}
                                />
                            </div>
                        )}
                        <div className="App-header">
                            <img src={companyLogo} className="Company-logo" alt="company-logo"/>
                            <img src={appLogo} className="App-logo" alt="company-logo"/>
                        </div>
                        <div className="App-body">
                            {errorMessage ? (
                                <div className="Mansion-video-message-container">
                                    <div className="Mansion-video-message">
                                        {errorMessage}
                                    </div>
                                </div>
                            ) : playbackUrl ? (
                                <div className="Mansion-video-container">
                                    <video
                                        className="Mansion-video-player"
                                        controls
                                        playsInline
                                    >
                                        <source src={playbackUrl} type="video/mp4"/>
                                    </video>
                                </div>
                            ) : null}
                        </div>
                    </div>
                </div>
            </div>
        </HelmetProvider>
    );
}

export default MansionVideoShare;
